import React, { useState, useEffect, useMemo, useRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import Pagination from '@mui/material/Pagination';
import '../shared.css';
import LoadingOverlay from '../LoadingOverlay';

function InterStateStockSummary() {
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();

    const [dataItem,        setDataItem]        = useState([]);
    const [searchQuery,     setSearchQuery]     = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [fromDate,        setFromDate]        = useState('');
    const [toDate,          setToDate]          = useState('');
    const [loading,         setLoading]         = useState(false);
    const [page,            setPage]            = useState(1);
    const rowsPerPage = 100;
    const loadingRef  = useRef(false);

    const hasReadReportPermission = permissions.includes('Read Reports');

    /* ── init dates ─────────────────────────────────────────── */
    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        const d     = new Date();
        const first = new Date(d.getFullYear(), d.getMonth(), 1);
        setFromDate(first.toISOString().split('T')[0]);
        setToDate(d.toISOString().split('T')[0]);
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        if (fromDate && toDate) fetchTableData(fromDate, toDate);
    }, [fromDate, toDate]);

    /* ── fetch ──────────────────────────────────────────────── */
    const fetchTableData = async (fDate, tDate) => {
        const f = fDate || fromDate;
        const t = tDate || toDate;
        if (!hasReadReportPermission || loadingRef.current || !f || !t) return;
        setDataItem([]);
        loadingRef.current = true;
        setLoading(true);
        try {
            const res = await fetchData(
                'ReportApi',
                `/InterCityStockSummary/${companyId}/${branchId}/${f}/${t}`,
                token
            );
            if (res.responseCode === '1111') setDataItem(res.response_data);
        } catch (e) {
            console.error('Error fetching Inter State Stock Summary:', e);
        } finally {
            loadingRef.current = false;
            setLoading(false);
        }
    };

    /* ── search + pagination ────────────────────────────────── */
    const filteredData = useMemo(() =>
        dataItem.filter(item =>
            Object.values(item).some(v =>
                String(v).toLowerCase().includes(searchQuery.toLowerCase())
            )
        ),
    [dataItem, searchQuery]);

    const paginatedData = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return filteredData.slice(start, start + rowsPerPage);
    }, [filteredData, page]);

    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    /* ── aggregated totals ──────────────────────────────────── */
    const totals = useMemo(() => {
        let qty = 0, taxable = 0, cgst = 0, sgst = 0, igst = 0,
            grand = 0, purchase = 0;
        const bills = new Set();
        filteredData.forEach(r => {
            qty      += r.Quantity      || 0;
            taxable  += r.TaxableAmount || 0;
            cgst     += r.CGSTAmount    || 0;
            sgst     += r.SGSTAmount    || 0;
            igst     += r.IGSTAmount    || 0;
            grand    += r.TotalAmount   || 0;
            purchase += r.PurchaseAmount|| 0;
            if (r.BillNumber) bills.add(r.BillNumber);
        });
        return { qty, taxable, cgst, sgst, igst, grand, purchase, bills: bills.size };
    }, [filteredData]);

    /* ── helpers ────────────────────────────────────────────── */
    const formatDate = d =>
        d ? new Date(d).toLocaleDateString('en-GB',
            { year: 'numeric', month: '2-digit', day: '2-digit' }) : '';
    const sf  = v  => (v  != null ? parseFloat(v).toFixed(2)  : '0.00');
    const sfN = v  => (v  != null ? parseFloat(v).toFixed(2)  : '');

    /* ── Excel column config ─────────────────────────────────── */
    const HEADERS = [
        'Date', 'Time', 'Memo No', 'Branch Name', 'Transfer Type',
        'PG', 'IG', 'ISG', 'PF',
        'Item No', 'Description', 'HSN Code', 'Quantity',
        'MRP', 'MRP On Net', 'Discount %', 'Discount Value',
        'Additional Disc %', 'Add Disc Value', 'Cash Discount',
        'Promo Disc %', 'Promo Disc Value',
        'Balance', 'GST %',
        'CGST Amount', 'SGST Amount', 'IGST Amount', 'Net Value',
        'Net Value + Tax',
        'Purchase Rate', 'Total Purchase Value',
        'Mode Of Payment', 'GST Number', 'Sales Man', 'Comments', 'GWP/PWP',
        'Inter-City Stock Transfer',
        'Category', 'PG Category', 'Group', 'Season Code', 'Size Code', 'Exclusion',
    ];

    const RIGHT_COLS = new Set([
        'Quantity', 'MRP', 'MRP On Net', 'Discount %', 'Discount Value',
        'Additional Disc %', 'Add Disc Value', 'Cash Discount',
        'Promo Disc %', 'Promo Disc Value', 'Balance',
        'GST %', 'CGST Amount', 'SGST Amount', 'IGST Amount',
        'Net Value', 'Net Value + Tax', 'Purchase Rate', 'Total Purchase Value',
    ]);

    /* ── Excel export ────────────────────────────────────────── */
    const handleExportExcel = async () => {
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Inter State Stock Summary');

        /* caption */
        ws.mergeCells(`A1:${String.fromCharCode(64 + HEADERS.length)}1`);
        const cap = ws.getCell('A1');
        cap.value = `Inter State Stock Transfer Summary   From: ${formatDate(fromDate)}   To: ${formatDate(toDate)}`;
        cap.font  = { bold: true, size: 13 };
        cap.alignment = { vertical: 'middle', horizontal: 'left' };

        /* header row */
        const hRow = ws.addRow(HEADERS);
        hRow.eachCell(cell => {
            cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB6D7A8' } };
            cell.font      = { bold: true };
            cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            cell.border    = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        ws.columns = HEADERS.map(() => ({ width: 18 }));

        /* data rows */
        filteredData.forEach(r => {
            const row = ws.addRow([
                formatDate(r.BillDate), r.BillTime, r.BillNumber,
                r.ToBranchName || '', r.TransferType || '',
                r.PG || '', r.IG || '', r.ISG || '', r.PF || '',
                r.Code || '', r.ItemName || '', r.HSNCode || '',
                r.Quantity || 0,
                parseFloat(sfN(r.Rate)),
                parseFloat(sfN(r.Amount)),
                r.DiscountPercentage || 0,
                parseFloat(sfN(r.DiscountAmount)),
                r.AdditionalDiscountPercentage || 0,
                parseFloat(sfN(r.AdditionalDiscountAmount)),
                parseFloat(sfN(r.CashDiscount)),
                r.PromotionalDiscountPercentage || 0,
                parseFloat(sfN(r.PromotionalDiscountAmount)),
                parseFloat(sfN(r.TotalAmount)),
                r.GSTRate || 0,
                parseFloat(sfN(r.CGSTAmount)),
                parseFloat(sfN(r.SGSTAmount)),
                parseFloat(sfN(r.IGSTAmount)),
                parseFloat(sfN(r.TaxableAmount)),
                parseFloat(sfN(r.TotalAmount)),
                parseFloat(sfN(r.PurchaseRate)),
                parseFloat(sfN(r.PurchaseAmount)),
                r.ModeOfPayment || '', r.GSTNumber || '',
                r.SalesManName || '', r.Remarks || '', r.Gwp || '',
                r.InterStockTransfer || '',
                r.CategoryName || '', r.PGCategoryName || '',
                r.ItemGroupName || '', r.SeasonCode || '',
                r.SizeCode || '', r.IsExcludingItem || '',
            ]);
            row.eachCell((cell, col) => {
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: RIGHT_COLS.has(HEADERS[col - 1]) ? 'right' : 'left',
                };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });

        /* totals row */
        const blankRow = new Array(HEADERS.length).fill('');
        blankRow[2]  = `Total Bills: ${totals.bills}`;
        blankRow[12] = totals.qty;
        blankRow[22] = parseFloat(totals.grand.toFixed(2));
        blankRow[24] = parseFloat(totals.cgst.toFixed(2));
        blankRow[25] = parseFloat(totals.sgst.toFixed(2));
        blankRow[26] = parseFloat(totals.igst.toFixed(2));
        blankRow[27] = parseFloat(totals.taxable.toFixed(2));
        blankRow[28] = parseFloat(totals.grand.toFixed(2));
        blankRow[30] = parseFloat(totals.purchase.toFixed(2));
        const tRow = ws.addRow(blankRow);
        tRow.font = { bold: true };
        tRow.eachCell((cell, col) => {
            cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
            cell.alignment = { vertical: 'middle', horizontal: RIGHT_COLS.has(HEADERS[col - 1]) ? 'right' : 'left' };
            cell.border    = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        const buf = await wb.xlsx.writeBuffer();
        saveAs(new Blob([buf]), 'InterStateStockSummary.xlsx');
    };

    /* ── render ─────────────────────────────────────────────── */
    return (
        <div className="pg-container">

            {/* ── Header ── */}
            <div className="pg-header">
                <h5 className="pg-title">Inter State Stock Transfer Summary</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate}
                                onChange={e => setFromDate(e.target.value)} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate}
                                onChange={e => setToDate(e.target.value)} />
                        </div>
                        <button className="pg-btn pg-btn-outline"
                            onClick={() => fetchTableData()} disabled={loading}>
                            <RefreshIcon /> {loading ? 'Loading…' : 'Refresh'}
                        </button>
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel}>
                            <FeedIcon /> Excel
                        </button>
                        <button className="pg-btn pg-btn-outline"
                            onClick={() => { setShowSearchInput(v => !v); setSearchQuery(''); }}>
                            <SearchIcon /> Search
                        </button>
                    </div>
                    {showSearchInput && (
                        <input className="pg-input pg-search-input" placeholder="Search…"
                            value={searchQuery}
                            onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
                            autoFocus style={{ marginTop: 6 }} />
                    )}
                </div>
            </div>

            {loading && <LoadingOverlay />}

            {/* ── Table ── */}
            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Time</th>
                            <th>Memo No</th>
                            <th>Branch Name</th>
                            <th>Transfer Type</th>
                            <th>PG</th>
                            <th>IG</th>
                            <th>ISG</th>
                            <th>PF</th>
                            <th>Item No</th>
                            <th>Description</th>
                            <th>HSN Code</th>
                            <th style={{ textAlign: 'right' }}>Quantity</th>
                            <th style={{ textAlign: 'right' }}>MRP</th>
                            <th style={{ textAlign: 'right' }}>MRP On Net</th>
                            <th style={{ textAlign: 'right' }}>Discount %</th>
                            <th style={{ textAlign: 'right' }}>Discount Value</th>
                            <th style={{ textAlign: 'right' }}>Add Disc %</th>
                            <th style={{ textAlign: 'right' }}>Add Disc Value</th>
                            <th style={{ textAlign: 'right' }}>Cash Discount</th>
                            <th style={{ textAlign: 'right' }}>Promo Disc %</th>
                            <th style={{ textAlign: 'right' }}>Promo Disc Value</th>
                            <th style={{ textAlign: 'right' }}>Balance</th>
                            <th style={{ textAlign: 'right' }}>GST %</th>
                            <th style={{ textAlign: 'right' }}>CGST Amount</th>
                            <th style={{ textAlign: 'right' }}>SGST Amount</th>
                            <th style={{ textAlign: 'right' }}>IGST Amount</th>
                            <th style={{ textAlign: 'right' }}>Net Value</th>
                            <th style={{ textAlign: 'right' }}>Net Value + Tax</th>
                            <th style={{ textAlign: 'right' }}>Purchase Rate</th>
                            <th style={{ textAlign: 'right' }}>Total Purchase Value</th>
                            <th>Mode Of Payment</th>
                            <th>GST Number</th>
                            <th>Sales Man</th>
                            <th>Comments</th>
                            <th>GWP/PWP</th>
                            <th>Inter-City Stock Transfer</th>
                            <th>Category</th>
                            <th>PG Category</th>
                            <th>Group</th>
                            <th>Season Code</th>
                            <th>Size Code</th>
                            <th>Exclusion</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length === 0 && !loading && (
                            <tr>
                                <td colSpan={43} style={{ textAlign: 'center', color: '#94a3b8', padding: 20 }}>
                                    No records found
                                </td>
                            </tr>
                        )}
                        {paginatedData.map((d, i) => (
                            <tr key={i}>
                                <td>{formatDate(d.BillDate)}</td>
                                <td>{d.BillTime}</td>
                                <td>{d.BillNumber}</td>
                                <td>{d.ToBranchName}</td>
                                <td>{d.TransferType}</td>
                                <td>{d.PG}</td>
                                <td>{d.IG}</td>
                                <td>{d.ISG}</td>
                                <td>{d.PF}</td>
                                <td>{d.Code}</td>
                                <td>{d.ItemName}</td>
                                <td>{d.HSNCode}</td>
                                <td style={{ textAlign: 'right' }}>{d.Quantity}</td>
                                <td style={{ textAlign: 'right' }}>{sf(d.Rate)}</td>
                                <td style={{ textAlign: 'right' }}>{sf(d.Amount)}</td>
                                <td style={{ textAlign: 'right' }}>{d.DiscountPercentage}</td>
                                <td style={{ textAlign: 'right' }}>{sf(d.DiscountAmount)}</td>
                                <td style={{ textAlign: 'right' }}>{d.AdditionalDiscountPercentage}</td>
                                <td style={{ textAlign: 'right' }}>{sf(d.AdditionalDiscountAmount)}</td>
                                <td style={{ textAlign: 'right' }}>{sf(d.CashDiscount)}</td>
                                <td style={{ textAlign: 'right' }}>{sf(d.PromotionalDiscountPercentage)}</td>
                                <td style={{ textAlign: 'right' }}>{sf(d.PromotionalDiscountAmount)}</td>
                                <td style={{ textAlign: 'right' }}>{sf(d.TotalAmount)}</td>
                                <td style={{ textAlign: 'right' }}>{d.GSTRate}</td>
                                <td style={{ textAlign: 'right' }}>{sf(d.CGSTAmount)}</td>
                                <td style={{ textAlign: 'right' }}>{sf(d.SGSTAmount)}</td>
                                <td style={{ textAlign: 'right' }}>{sf(d.IGSTAmount)}</td>
                                <td style={{ textAlign: 'right' }}>{sf(d.TaxableAmount)}</td>
                                <td style={{ textAlign: 'right' }}>{sf(d.TotalAmount)}</td>
                                <td style={{ textAlign: 'right' }}>{sf(d.PurchaseRate)}</td>
                                <td style={{ textAlign: 'right' }}>{sf(d.PurchaseAmount)}</td>
                                <td>{d.ModeOfPayment}</td>
                                <td>{d.GSTNumber}</td>
                                <td>{d.SalesManName}</td>
                                <td>{d.Remarks}</td>
                                <td>{d.Gwp}</td>
                                <td>{d.InterStockTransfer}</td>
                                <td>{d.CategoryName}</td>
                                <td>{d.PGCategoryName}</td>
                                <td>{d.ItemGroupName}</td>
                                <td>{d.SeasonCode}</td>
                                <td>{d.SizeCode}</td>
                                <td>{d.IsExcludingItem}</td>
                            </tr>
                        ))}

                        {/* Totals row */}
                        {filteredData.length > 0 && (
                            <tr style={{ background: '#1c2a3a', color: '#fff', fontWeight: 700 }}>
                                <td colSpan={7}></td>
                                <td>Total Bills</td>
                                <td>{totals.bills}</td>
                                <td colSpan={3}></td>
                                <td style={{ textAlign: 'right' }}>{totals.qty}</td>
                                <td colSpan={9}></td>
                                <td style={{ textAlign: 'right' }}>{totals.grand.toFixed(2)}</td>
                                <td></td>
                                <td style={{ textAlign: 'right' }}>{totals.cgst.toFixed(2)}</td>
                                <td style={{ textAlign: 'right' }}>{totals.sgst.toFixed(2)}</td>
                                <td style={{ textAlign: 'right' }}>{totals.igst.toFixed(2)}</td>
                                <td style={{ textAlign: 'right' }}>{totals.taxable.toFixed(2)}</td>
                                <td style={{ textAlign: 'right' }}>{totals.grand.toFixed(2)}</td>
                                <td></td>
                                <td style={{ textAlign: 'right' }}>{totals.purchase.toFixed(2)}</td>
                                <td colSpan={12}></td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {filteredData.length > rowsPerPage && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 20, gap: 20 }}>
                    <span style={{ fontSize: 13, color: '#666' }}>
                        Showing {((page - 1) * rowsPerPage) + 1}–{Math.min(page * rowsPerPage, filteredData.length)} of {filteredData.length} records
                    </span>
                    <Pagination count={totalPages} page={page}
                        onChange={(_, v) => setPage(v)}
                        color="primary" showFirstButton showLastButton />
                </div>
            )}

            <div className="pg-watermark">
                <CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b>
            </div>
        </div>
    );
}

export default InterStateStockSummary;
